import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { Flex } from 'rebass/styled-components';
import { useParams } from 'react-router-dom';

import AccountsList from './AccountsList';
import SelectedProceduresList from './SelectedProcedureList';
import PreviewPanel from './PreviewPanel';

import { Loader, SlidePanel } from 'ui/patterns';
import { Button } from 'ui/components';

import {
  useGetAccountListForLinkingProcedures,
  useGetProcedureDetails,
  useLinkProcedures,
} from 'hooks';

import { confirmAlert } from 'utils/confirm-alert';
import { showErrorToast, showSuccessToast } from 'utils/showToast';

import { SelectedAccounts } from 'types/procedureBuilder';
import { ErrorModal } from 'types/modalTypes';
import { LinkProcedureProps } from './linkProcedureTypes';

export default function LinkProcedure({
  showPanel,
  togglePanel: toggle,
  accountInfo,
}: LinkProcedureProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [checkedAccounts, setCheckedAccounts] = useState<
    Array<SelectedAccounts>
  >([]);
  const [previewProcedure, setPreviewProcedure] = useState(false);
  const [procedureHeader, setProcedureHeader] = useState(false);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [selectedIds, setSelectetIds] = useState<{
    accountId: number;
    procedureId: number;
  }>();
  const [checkState, setCheckState] = useState(false);

  const params = useParams<{
    accountId: string;
  }>();
  const accountId = parseInt(params.accountId);

  const {
    data: procedureList,
    fetchNextPage,
    hasNextPage,
    isLoading: isProcedureDataLoading,
    isFetching,
  } = useGetAccountListForLinkingProcedures(accountId, keywordSearch);

  const existingAccounts = useMemo(() => {
    if (procedureList) {
      return procedureList?.pages?.map((dataItem: any) => dataItem.Data).flat();
    } else return [];
  }, [procedureList]);

  const [expanded, setExpanded] = useState(() => {
    return existingAccounts.map(() => false);
  });

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  const { selectedAccountId, selectedProcedureId } = useMemo(() => {
    const selectedAccounts = checkedAccounts.filter(item =>
      item?.accountProcedures?.some(
        accountItem => accountItem.isChecked === true,
      ),
    );
    if (selectedAccounts && selectedAccounts.length === 1) {
      const selectedProcedures =
        selectedAccounts[0] &&
        selectedAccounts[0].accountProcedures &&
        selectedAccounts[0].accountProcedures.filter(
          item => item.isChecked === true,
        );
      if (selectedProcedures && selectedProcedures.length === 1)
        return {
          selectedAccountId: selectedAccounts[0].id,
          selectedProcedureId: selectedProcedures[0].ProcedureId,
        };
    }
    return {
      selectedAccountId: undefined,
      selectedProcedureId: undefined,
    };
  }, [checkedAccounts]);

  const { mutate: linkAccountProcedures, isLoading: isLinkProcedureLoading } =
    useLinkProcedures();

  const { data: procedureDetails, isLoading: isProcedureDetailsLoading } =
    useGetProcedureDetails({
      accountId: selectedAccountId,
      procedureId: selectedProcedureId,
    });

  const openPreviewProcedure = (
    accountId: number | undefined,
    procedureId: number | undefined,
  ) => {
    setPreviewProcedure(true);

    if (procedureId && accountId)
      setSelectetIds({
        procedureId: procedureId,
        accountId: accountId,
      });
  };

  const closePreviewProcedure = () => {
    setPreviewProcedure(false);
  };

  const isLoading = isProcedureDataLoading || isFetching;

  const onEndReached = () => {
    if (hasNextPage) fetchNextPage();
  };

  const linkProcedures = () => {
    let checkedProcedures = checkedAccounts
      ?.map(accounts =>
        accounts?.accountProcedures?.map(procedure => {
          if (procedure.isChecked) {
            return {
              Name: procedure.ProcedureName,
              AccountId: accounts.id,
              ProcedureId: procedure.ProcedureId,
            };
          } else return;
        }),
      )
      .flat();
    checkedProcedures = checkedProcedures?.filter(item => item !== undefined);
    confirmAlert({
      message: 'Are you sure you want to link the procedure(s)?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            linkAccountProcedures(
              {
                accountId,
                payload: {
                  AccountName: accountInfo.accountName,
                  CID: accountInfo.cid,
                  SID: accountInfo.sid,
                  PID: accountInfo.pid,
                  LinkProcedures: checkedProcedures,
                },
              },

              {
                onSuccess: () => {
                  showSuccessToast({
                    message: 'Procedure(s) Linked Successfully.',
                  });
                  setCheckedAccounts([]);
                  collapseAll();
                  togglePanel();
                },
                onError: (error: ErrorModal) => {
                  showErrorToast({
                    message: error.Errors[0].Message,
                  });
                },
              },
            );
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  const onClickExpanded = (index: number, opened: boolean) => {
    const newExpanded = [...expanded];
    newExpanded[index] = opened;
    setExpanded([...newExpanded]);
  };

  const collapseAll = () => {
    setExpanded(existingAccounts.map(() => false));
  };

  const reset = () => {
    togglePanel && togglePanel();
    setCheckedAccounts([]);
    collapseAll();
    setKeywordSearch('');
    setCheckState(false);
  };

  const panelActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          reset();
        }}
      >
        Cancel
      </Button>
      <Button
        disabled={(() => {
          const checkedProcedures = checkedAccounts
            ?.map(accounts =>
              accounts?.accountProcedures?.map(procedure => {
                if (procedure.isChecked) {
                  return procedure.ProcedureId;
                } else return;
              }),
            )
            .flat();
          if (checkedProcedures.length > 0) return false;
          return true;
        })()}
        onClick={linkProcedures}
        variant="primary"
      >
        {'Link'}
      </Button>
    </>
  );

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        reset();
      }}
      size="lg"
      title="Link Procedure"
      actions={panelActions()}
    >
      <Flex flexDirection="column" height="100%">
        <Flex height="100%" flex="1 1 auto">
          <AccountsList
            checkState={checkState}
            setCheckState={setCheckState}
            keywordSearch={keywordSearch}
            procedureListData={existingAccounts}
            checkedAccounts={checkedAccounts}
            setCheckedAccounts={setCheckedAccounts}
            onEndReached={onEndReached}
            setProcedureHeader={setProcedureHeader}
            setKeywordSearch={setKeywordSearch}
            expanded={expanded}
            setExpanded={setExpanded}
            onClickExpanded={onClickExpanded}
            collapseAll={collapseAll}
          />
          <SelectedProceduresList
            procedureHeader={procedureHeader}
            checkedAccounts={checkedAccounts}
            procedureDetails={procedureDetails?.Data}
            isProcedureDetailsLoading={isProcedureDetailsLoading}
            setCheckedAccounts={setCheckedAccounts}
            togglePreviewProcedure={openPreviewProcedure}
          />
        </Flex>
        <PreviewPanel
          previewProcedure={previewProcedure}
          closePreviewProcedure={closePreviewProcedure}
          selectedIds={selectedIds}
        />
        {(isLoading || isLinkProcedureLoading) && <Loader />}
      </Flex>
    </SlidePanel>
  );
}
